import { useEffect, useState } from "react";
import { workSessionService } from "../api/work-session.service";
import {
  StopwatchAvailabilityStatus,
  stopwatchAvailabilityMessage,
} from "../model";

type AvailabilityState = {
  status: StopwatchAvailabilityStatus;
  warning: string | null;
  loading: boolean;
};

const DEFAULT_STATE: AvailabilityState = {
  status: "available",
  warning: null,
  loading: false,
};

export function useStopwatchAvailability(collaboratorId?: number) {
  const [state, setState] = useState<AvailabilityState>(DEFAULT_STATE);

  useEffect(() => {
    // No collaborator means the modal cannot evaluate availability yet, so reset to the neutral state.
    if (!collaboratorId) {
      setState(DEFAULT_STATE);
      return;
    }

    let cancelled = false;

    // Fetch availability once the stopwatch modal opens for the active collaborator.
    setState((current) => ({
      ...current,
      loading: true,
    }));

    const fetchAvailability = async () => {
      try {
        const response = await workSessionService.getStopwatchAvailability();

        if (cancelled) return;

        const status = response.status;

        // Map backend state to a single warning string without exposing remaining hours.
        setState({
          status,
          warning: stopwatchAvailabilityMessage[status],
          loading: false,
        });
      } catch (error) {
        if (cancelled) return;

        // On fetch failure, fall back to the neutral state so save-time validation remains authoritative.
        setState(DEFAULT_STATE);
        console.error(error);
      }
    };

    fetchAvailability();

    return () => {
      cancelled = true;
    };
  }, [collaboratorId]);

  return state;
}
